import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, KeyRound, User, Eye, EyeOff, Loader2 } from "lucide-react";
import { LOGO_URL, APP_NAME, APP_SUB } from "@/components/common";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.6-.2-2.36H12v4.46h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.73z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.26v3.09A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56v-3.1H1.26a12 12 0 0 0 0 10.76l4.01-3.1z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.23 0 12 0A12 12 0 0 0 1.26 6.62l4.01 3.1C6.22 6.86 8.87 4.75 12 4.75z" />
    </svg>
  );
}

export default function StaffLogin() {
  const { user, loading, staffLogin, staffVerifyOtp, staffGoogle } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [otp, setOtp] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [gbusy, setGbusy] = useState(false);
  const processed = useRef(false);

  useEffect(() => { if (!loading && user) navigate("/dashboard", { replace: true }); }, [user, loading, navigate]);

  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=") || processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    window.history.replaceState({}, "", window.location.pathname);
    if (!sid) return;
    setGbusy(true);
    (async () => {
      try {
        const r = await staffGoogle(sid);
        if (r?.otp_required) {
          setOtp(r); setGbusy(false);
          if (r.simulation) toast.info(`MODE SIMULASI — kode OTP: ${r.debug_code}`, { duration: 20000 });
          else toast.success(`OTP dikirim ke WA ${r.masked_wa}`);
          return;
        }
        toast.success("Berhasil masuk dengan Google"); navigate("/dashboard", { replace: true });
      } catch (err) { toast.error(errMsg(err, "Login Google gagal")); setGbusy(false); }
    })();
  }, [staffGoogle, navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/pengurus";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await staffLogin(username.trim(), password);
      if (r.otp_required) {
        setOtp(r);
        if (r.simulation) toast.info(`MODE SIMULASI — kode OTP: ${r.debug_code}`, { duration: 20000 });
        else toast.success(`OTP dikirim ke WA ${r.masked_wa}`);
      } else { toast.success(`Selamat datang, ${r.name}`); navigate("/dashboard", { replace: true }); }
    } catch (err) { toast.error(errMsg(err, "Login gagal")); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const u = await staffVerifyOtp(otp.challenge_id, code); toast.success(`Selamat datang, ${u.name}`); navigate("/dashboard", { replace: true }); }
    catch (err) { toast.error(errMsg(err, "OTP salah")); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{ background: theme === "dark"
        ? "linear-gradient(160deg, #090D09 0%, #0F1C0A 55%, #090D09 100%)"
        : "linear-gradient(135deg, #173404 0%, #3B6D11 60%, #639922 100%)" }}>
      <ThemeToggle className="absolute top-4 right-4 z-30 bg-white/15 border-white/25 text-white hover:bg-white/25" />
      <motion.div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-lime-300/20 blur-3xl" animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 8, repeat: Infinity }} />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-3 mb-6 text-white">
          <img src={LOGO_URL} alt="Logo" className="h-12 w-12 object-contain drop-shadow-lg" />
          <div><p className="font-extrabold text-xl">{APP_NAME}</p><p className="text-lime-200 text-xs">{APP_SUB} · Portal Pengurus</p></div>
        </div>
        <div className="glass rounded-3xl p-7 sm:p-8" data-testid="staff-login-card">
          <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 mb-1"><ShieldCheck className="h-5 w-5" /><h1 className="text-2xl font-extrabold tracking-tight">Masuk Pengurus</h1></div>
          <p className="text-slate-500 dark:text-slate-300 text-sm mb-6">Khusus staf pondok. Gunakan username & sandi yang diberikan Super Admin.</p>
          {gbusy ? (
            <div className="flex flex-col items-center gap-3 py-6 text-emerald-800 dark:text-emerald-300" data-testid="staff-google-processing">
              <Loader2 className="h-7 w-7 animate-spin" />
              <p className="text-sm">Memproses login Google…</p>
            </div>
          ) : !otp ? (
            <form onSubmit={submit} className="space-y-3">
              <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600" />
                <Input data-testid="staff-username-input" required autoComplete="username" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} className="pl-9 h-12 rounded-xl bg-white/70" /></div>
              <div className="relative"><KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600" />
                <Input data-testid="staff-password-input" required type={show ? "text" : "password"} autoComplete="current-password" placeholder="Kata sandi" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 pr-11 h-12 rounded-xl bg-white/70" />
                <button type="button" aria-label="tampilkan sandi" data-testid="staff-toggle-password" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-700 p-1">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div>
              <Button type="submit" data-testid="staff-login-submit" disabled={busy} className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-600 to-lime-500 hover:from-emerald-700 hover:to-lime-600 text-white font-semibold shadow-lg shadow-emerald-900/20 transition-all">{busy ? "Memproses..." : "Masuk"}</Button>
              <div className="flex items-center gap-3 py-1"><span className="h-px flex-1 bg-slate-200/80 dark:bg-white/10" /><span className="text-[11px] text-slate-400 dark:text-slate-400">atau</span><span className="h-px flex-1 bg-slate-200/80 dark:bg-white/10" /></div>
              <Button type="button" variant="outline" data-testid="staff-google-login" onClick={googleLogin} className="w-full h-12 rounded-xl gap-2 bg-white/80 border-slate-200 text-slate-700 hover:bg-white hover:border-emerald-300 transition-all"><GoogleIcon /> Masuk dengan Google</Button>
              <p className="text-[11px] text-slate-400 dark:text-slate-400 text-center">Hanya email pengurus terdaftar yang dapat masuk lewat Google.</p>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4" data-testid="staff-otp-form">
              <p className="text-sm text-slate-600 dark:text-slate-300">Kode OTP telah dikirim ke WhatsApp <strong>{otp.masked_wa}</strong>. Berlaku 5 menit.</p>
              <Input data-testid="staff-otp-input" inputMode="numeric" maxLength={6} autoFocus placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-14 text-center text-2xl tracking-[0.5em] rounded-xl bg-white/70" />
              <Button type="submit" data-testid="staff-otp-submit" disabled={busy || code.length !== 6} className="w-full h-12 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white">Verifikasi & Masuk</Button>
              <button type="button" onClick={() => { setOtp(null); setCode(""); }} className="text-sm text-slate-500 dark:text-slate-400 underline w-full">Kembali</button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-lime-100/70 mt-6">Wali santri? <a href="/" className="underline">Masuk di halaman utama</a></p>
      </motion.div>
    </div>
  );
}
